import {
  faArrowSquareLeft,
  faArrowSquareRight
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MarkdownComponent from 'components/shared/MarkdownComponent';
import { useI18n } from 'hooks/useI18n';
import { UIOfferEvolution } from 'interfaces/ui/ui-offer-evolution';
import { useState } from 'react';
import { Heading4 } from '../../shared/Typography';
import {
  StyledRequirementsEvolution,
  StyledRequirementsEvolutionClient,
  StyledRequirementsEvolutionContainer,
  StyledRequirementsEvolutionContent,
  StyledRequirementsEvolutionDay0,
  StyledRequirementsEvolutionTitle,
  StyledRequirementsEvolutionTrigger,
  StyledRequirementsMilestone,
  StyledRequirementsMilestonesArrows,
  StyledRequirementsMilestonesContainer
} from './RequirementsEvolution.styled';
import RequirementsEvolutionGraphic from './RequirementsEvolutionGraphic';

const RequirementsEvolution = ({
  companyLogo,
  companyName,
  evolution,
  migratedToMarkdown
}: {
  migratedToMarkdown: boolean;
  companyLogo?: string;
  companyName?: string;
  evolution: UIOfferEvolution;
}) => {
  const { t } = useI18n('job-offer');

  const [activeMilestone, setActiveMilestone] = useState(1);

  function setMilestone(milestoneNumber: number): void {
    setActiveMilestone(milestoneNumber);
  }

  function nextMilestone(): void {
    if (activeMilestone < 3) {
      setActiveMilestone(activeMilestone + 1);
    }
  }

  function prevMilestone(): void {
    if (activeMilestone !== 1) {
      setActiveMilestone(activeMilestone - 1);
    }
  }

  return (
    <StyledRequirementsEvolution>
      <StyledRequirementsEvolutionTitle>
        {t('requisites.evolution.yourEvolution')}
      </StyledRequirementsEvolutionTitle>
      <StyledRequirementsEvolutionContainer>
        <RequirementsEvolutionGraphic dataMilestone={activeMilestone} />
        <StyledRequirementsEvolutionContent data-milestone={activeMilestone}>
          {companyLogo && (
            <StyledRequirementsEvolutionClient
              src={companyLogo}
              alt={companyName}
            />
          )}
          <StyledRequirementsEvolutionDay0>
            {t('requisites.day0')}
          </StyledRequirementsEvolutionDay0>
          <StyledRequirementsEvolutionTrigger
            className={`is-month-one ${
              activeMilestone === 1 ? 'is-active' : ''
            }`}
            aria-label={t('requisites.evolution.1month')}
            onClick={() => setMilestone(1)}
          />
          <StyledRequirementsEvolutionTrigger
            className={`is-month-three ${
              activeMilestone === 2 ? 'is-active' : ''
            }`}
            aria-label={t('requisites.evolution.3months')}
            onClick={() => setMilestone(2)}
          />
          <StyledRequirementsEvolutionTrigger
            className={`is-month-six ${
              activeMilestone === 3 ? 'is-active' : ''
            }`}
            aria-label={t('requisites.evolution.6months')}
            onClick={() => setMilestone(3)}
          />
        </StyledRequirementsEvolutionContent>
      </StyledRequirementsEvolutionContainer>
      <StyledRequirementsMilestonesContainer>
        <StyledRequirementsMilestonesArrows>
          <button
            type="button"
            onClick={() => prevMilestone()}
            aria-label={t('requisites.evolution.prev')}
            id="requirements-evolution-prev"
            disabled={activeMilestone === 1}
          >
            <FontAwesomeIcon aria-hidden="true" icon={faArrowSquareLeft} />
          </button>
          <button
            type="button"
            onClick={() => nextMilestone()}
            disabled={activeMilestone === 3}
            id="requirements-evolution-next"
            aria-label={t('requisites.evolution.next')}
          >
            <FontAwesomeIcon aria-hidden="true" icon={faArrowSquareRight} />
          </button>
        </StyledRequirementsMilestonesArrows>
        {activeMilestone === 1 && (
          <StyledRequirementsMilestone>
            <Heading4>{t('requisites.evolution.1month')}</Heading4>
            {migratedToMarkdown ? (
              <MarkdownComponent content={evolution.month1 || ''} />
            ) : (
              <div
                dangerouslySetInnerHTML={{ __html: evolution.month1 || '' }}
              ></div>
            )}
          </StyledRequirementsMilestone>
        )}
        {activeMilestone === 2 && (
          <StyledRequirementsMilestone>
            <Heading4>{t('requisites.evolution.3months')}</Heading4>
            {migratedToMarkdown ? (
              <MarkdownComponent content={evolution.month3 || ''} />
            ) : (
              <div
                dangerouslySetInnerHTML={{ __html: evolution.month3 || '' }}
              ></div>
            )}
          </StyledRequirementsMilestone>
        )}
        {activeMilestone === 3 && (
          <StyledRequirementsMilestone>
            <Heading4>{t('requisites.evolution.6months')}</Heading4>
            {migratedToMarkdown ? (
              <MarkdownComponent content={evolution.month6 || ''} />
            ) : (
              <div
                dangerouslySetInnerHTML={{ __html: evolution.month6 || '' }}
              ></div>
            )}
          </StyledRequirementsMilestone>
        )}
      </StyledRequirementsMilestonesContainer>
    </StyledRequirementsEvolution>
  );
};

export default RequirementsEvolution;
